import * as React from "react";

import { cn } from "@/lib/utils";

/** Wide record tables become labelled cards on touch-sized screens. Financial
 * comparison tables can opt out with responsive={false}. Headers stay available
 * to assistive technology; cell content and all actions are preserved. */
const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement> & { responsive?: boolean }>(
	({ className, children, responsive = true, ...props }, ref) => {
		const localRef = React.useRef<HTMLTableElement>(null);
		React.useImperativeHandle(ref, () => localRef.current as HTMLTableElement);
		React.useLayoutEffect(() => {
			const table = localRef.current;
			if (!table) return;
			const headers = Array.from(table.querySelectorAll("thead tr:first-child th"));
			table.dataset.records = String(responsive && headers.length >= 6);
			table.querySelectorAll("tbody tr").forEach(row => {
				let column = 0;
				Array.from(row.children).forEach(cell => {
					const td = cell as HTMLTableCellElement;
					td.dataset.label = td.colSpan > 1 ? "" : headers[column]?.textContent?.trim() || "";
					column += td.colSpan;
				});
			});
		}, [children, responsive]);
		return (
			<div className="record-table-container relative w-full overflow-auto rounded-xl">
				<table
					ref={localRef}
					role="table"
					className={cn("record-table w-full bg-card caption-bottom text-sm", className)}
					{...props}
				>
					{children}
				</table>
			</div>
		);
	},
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<thead
			ref={ref}
			className={cn("bg-muted/30 [&_tr]:border-b [&_tr]:border-border/60 [&_tr]:hover:bg-transparent", className)}
			{...props}
		/>
	),
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
	),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
	({ className, ...props }, ref) => (
		<tfoot
			ref={ref}
			className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)}
			{...props}
		/>
	),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
	({ className, ...props }, ref) => (
		<tr
			role="row"
			ref={ref}
			className={cn(
				"border-b border-border/60 transition-colors hover:bg-primary/5 data-[state=selected]:bg-muted",
				className,
			)}
			{...props}
		/>
	),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
	({ className, ...props }, ref) => (
		<th
			role="columnheader"
			scope="col"
			ref={ref}
			className={cn(
				"h-11 whitespace-nowrap px-4 align-middle text-xs font-semibold text-muted-foreground [&:has([role=checkbox])]:pr-0",
				className,
			)}
			{...props}
		/>
	),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
	({ className, ...props }, ref) => (
		<td
			role="cell"
			ref={ref}
			className={cn("px-4 py-3 align-middle [&:has([role=checkbox])]:pr-0", className)}
			{...props}
		/>
	),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
	({ className, ...props }, ref) => (
		<caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
	),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
